import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { ExportCisContractsRow } from '@ebalance/proto/bffuser/v1/cis_contract_model_pb'
import type { VoltageClass } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'

import {
  type CisContractCsvColumn,
  areaCsvValue,
  cisContractCsvDataTypeName,
  serializeCisContractCsvExportRows,
  voltageClassCsvValue,
} from '~/models/cisContractCsvHeader'
import { formatCivilDateSlash } from '~/models/civilDate'
import { commonUploadCsvFileName } from '~/pages/AdminUploads/models/downloadFileName'

/**
 * 供給地点マスタ (CIS契約マスタ) CSV エクスポートの行変換・CSV 組み立て
 * (uploads/common.md §6 AC-DUC-028/029、列定義は
 * docs/specs/admin/data/uploads/supply-point-master.md §1)。
 *
 * 出力列はアップロード CSV と同一の 8 列形式 (供給地点特定番号, エリア, 契約種別コード,
 * 電圧階級, 開始日, 終了日, 事業者コード, delete) に寄せる
 * (2026-08-25 PdM 確定、issue #6105)。エリア・電圧階級はアップロード側パーサ
 * (`application/usecase/ciscontract/csv.go` の parseRow が呼ぶ
 * `valueobject.AreaCodeFromCsvLabel` / `VoltageClassFromCsvLabel`) が
 * 受理する名前で書く (= コードでなく名前にしないと再アップロードで往復しない)。
 * 英語表示ではヘッダー行・エリア・電圧階級を英語で出す (`common.md` §6、
 * `conventions.md`「CSV の英語表示」)。画面の訳語 (i18n) ではなく CSV の値の表を
 * 引くのは、取込が受理する値と出力する値を同じ表に揃えるため。delete 列はエクスポート対象が
 * 論理削除されていない行のみ (backend `ListAllForExport` の doc 参照) なので
 * 常に "0" を書けば足りる。事業者コード列は未整備 (unset) の場合は空欄を書く。
 */

/**
 * CSV 1 行分のデータ。エリア・電圧階級は enum のまま持ち、CSV を組み立てる時に
 * 表示言語の CSV の値 (`~/models/cisContractCsvHeader` の `areaCsvValue` /
 * `voltageClassCsvValue`) へ写す。
 */
export type CisContractCsvExportRow = {
  supplyPointSpecificNumber: string
  areaCode: AreaCode
  cisTariffCode: string
  voltageClass: VoltageClass
  /** "YYYY/MM/DD" (アップロード CSV の日付形式、AC-DUI-004)。 */
  startDate: string
  /** "YYYY/MM/DD"。9999/12/31 は契約終了日未定センチネル (supply-point-master.md §1 列フォーマット)。 */
  endDate: string
  /** 決定16 の optional 8 列目。未整備 (unset) は空文字。 */
  ownerBusinessCode: string
}

export function toCisContractCsvExportRow(
  row: ExportCisContractsRow,
): CisContractCsvExportRow {
  return {
    supplyPointSpecificNumber: row.supplyPointSpecificNumber,
    areaCode: row.areaCode,
    cisTariffCode: row.cisTariffCode,
    voltageClass: row.voltageClass,
    startDate: formatCivilDateSlash(row.startDate),
    endDate: formatCivilDateSlash(row.endDate),
    ownerBusinessCode: row.ownerBusinessCode ?? '',
  }
}

/** 応答全行を CSV 行へ変換する (順序は backend が決めた並びを保つ)。 */
export function toCisContractCsvExportRows(
  rows: readonly ExportCisContractsRow[],
): CisContractCsvExportRow[] {
  return rows.map(toCisContractCsvExportRow)
}

/**
 * エクスポート行から CSV 本文を組み立てる (F1 純関数)。
 *
 * 列順・エスケープ・改行規約 (カンマ区切り、`\r\n`、RFC 4180) は
 * `~/models/cisContractCsvHeader` の `serializeCisContractCsvExportRows`
 * (単一 SoT) に委譲する — 雛形 DL 側の `serializeCisContractCsvTemplateRows`
 * と同じ内部実装を呼ぶため、2 経路で改行規約がずれることは構造的に起きない
 * (issue #6467 レビュー指摘)。全列必須の `Record` で渡すため、
 * `CIS_CONTRACT_CSV_COLUMNS` に列を足すとこの関数の row 組み立てが型エラーで
 * 追従漏れを検出する (列名 key の row を渡すだけで、CSV セルの位置を手書きしない)。
 * BOM は付けない (= `~/libs/downloadCsv` が保存時に付与する)。
 */
export function buildCisContractCsvExport(
  rows: readonly CisContractCsvExportRow[],
  isEnglish: boolean,
): string {
  const records: Array<Record<CisContractCsvColumn, string>> = rows.map(
    (row) => ({
      供給地点特定番号: row.supplyPointSpecificNumber,
      エリア: areaCsvValue(row.areaCode, isEnglish),
      契約種別コード: row.cisTariffCode,
      電圧階級: voltageClassCsvValue(row.voltageClass, isEnglish),
      開始日: row.startDate,
      終了日: row.endDate,
      事業者コード: row.ownerBusinessCode, // 決定16: 未整備は空欄
      delete: '0', // エクスポート対象は論理削除されていない行のみ
    }),
  )
  return serializeCisContractCsvExportRows(records, isEnglish)
}

/**
 * 入稿済みデータのファイル名 (`{データ種別名}_{YYYYMMDD}.csv`、common.md §6。
 * 英語表示は英語データ種別名 `supply-point-master`)。
 */
export function cisContractCsvExportFileName(
  isEnglish: boolean,
  today?: Date,
): string {
  return commonUploadCsvFileName(cisContractCsvDataTypeName(isEnglish), today)
}
